import { useContext, useReducer, createContext, useCallback } from "react";
import { useRequest } from "../../services/request";

const Context = createContext();

export function useSubCompany() {
  return useContext(Context);
}

const initState = {
  list: [],
  Names: [],
  selected: null,
  selectedfile: null,
  filter: { companyId: 0 },
  count: 0,
  page: 1,
  perPage: 10,
  totalSize: 0,
  status: "idle",
  error: null,
};
const reducer = (state, action) => {
  switch (action.type) {
    case "set_list":
      return { ...state, list: [...action.payload] };
    case "set_Names":
      return { ...state, Names: [...action.payload] };
    case "set_selected":
      return { ...state, selected: { ...action.payload } };
    case "set_selectedfile":
      return { ...state, selectedfile: { ...action.payload } };
    case "set_count":
      return { ...state, count: action.payload };
    case "set_filter":
      return { ...state, filter: action.payload };
    case "add":
      const newList = [{ ...action.payload }, ...state.list];
      return { ...state, list: newList };
    case "edit":
      const modified = state.list.map((p) =>
        p.id === action.payload.id ? { ...p, ...action.payload } : p
      );
      return { ...state, list: modified };
    case "delete":
      const filtered = state.list.filter((p) => p.id !== action.payload);
      return { ...state, list: filtered };
    case "status":
      return { ...state, status: action.payload };
    case "set_totalSize":
      return { ...state, totalSize: action.payload };
    case "set_page":
      return { ...state, page: action.payload };
    case "set_per_page":
      return { ...state, perPage: action.payload };

    default:
      throw new Error(`Invalid dispatch type: ${action.type}`);
  }
};

export default function SubCompanyProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const req = useRequest();

  const fetchList = useCallback(
    async (cid = 1, page = 1, perPage = state.perPage) => {
      if (perPage === 0) perPage = 10;
      let headersData = {
        pageNumber: state.page,
        pageSize: perPage,
      };
      dispatch({ type: "status", payload: "fetching" });
      const resData = await req(
        `SubCompany/List/${cid}`,
        null,
        {},
        true,
        headersData
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_list", payload: resData?.data?.subCompanies || [] });
      dispatch({ type: "set_count", payload: resData.data.totalPages || 1 });
      dispatch({
        type: "set_totalSize",
        payload: resData.data.totalSize || 0,
      });
    },
    [req, state.page, state.perPage]
  );
  /**fetch names */

  const fetchNames = useCallback(
    async (cid = 1) => {
      dispatch({ type: "status", payload: "fetching" });
      const resData = await req(`SubCompany/Names/${cid}`, null, {}, true, {});
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_Names", payload: resData.data || [] });
    },
    [req]
  );
  const fetchOne = useCallback(
    (id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `fetching` });
        const resData = await req(`SubCompany/Info/${id}`, null, {}, true);
        dispatch({ type: "set_selected", payload: resData.data });
        dispatch({ type: "status", payload: `idle` });
        resolve(resData);
      });
    },
    [req]
  );

  const create = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        try {
          const newData = { ...data };
          const resData = await req(
            "SubCompany/Create",
            newData,
            { method: "POST" },
            true
          );
          dispatch({ type: "add", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const edit = useCallback(
    async (id, data) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `SubCompany/Update/${id}`,
            data,
            { method: "PUT" },
            true
          );
          dispatch({ type: "edit", payload: resData });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const deleting = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          const resData = await req(
            `SubCompany/Delete/${id}`,
            null,
            { method: "DELETE" },
            true
          );
          dispatch({ type: "delete", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );

  return (
    <Context.Provider
      value={{
        state,
        fetchList,
        fetchNames,
        fetchOne,
        edit,
        create,
        deleting,
        dispatch,
      }}
    >
      {children}
    </Context.Provider>
  );
}
